import React from 'react'
import classNames from 'classnames'
import { Portal } from '@material-ui/core'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useLoginInteractor } from '../login/interactor'
import { useAuthenticateUseCase } from '../../use-cases'
import { useSessionStore } from '../../stores'
import { AnalyticsEvent, useAnalytics } from '@aninix/analytics'

export const SuggestLogin: React.FCC<{}> = ({}) => {
  const [isShown, setIsShown] = React.useState(false)
  const [appearTransition, setAppearTransition] = React.useState(false)

  const [searchParams] = useSearchParams()
  const sessionStore = useSessionStore()
  const authenticate = useAuthenticateUseCase({ sessionStore })
  const analytics = useAnalytics()

  const pathname = useLocation().pathname
  const urlToRedirect = `/${pathname}`

  const interactor = useLoginInteractor({
    addAccount: false,
    authenticate,
    searchParams,
  })

  const onLoginWithFigma = () => {
    analytics.track({
      eventName: AnalyticsEvent.LoginSuggestionLoginClicked,
    })
    interactor.login(urlToRedirect)
  }

  const onContinueAnyway = () => {
    analytics.track({
      eventName: AnalyticsEvent.LoginSuggestionContinueAnywayClicked,
    })
    sessionStore.skipLoginSuggestion()
    setIsShown(false)
  }

  React.useEffect(() => {
    sessionStore.shouldShowLoginSuggestion().then((shouldShow) => {
      if (shouldShow) {
        analytics.track({
          eventName: AnalyticsEvent.LoginSuggestionViewed,
        })
        setIsShown(true)
        setTimeout(() => {
          setAppearTransition(true)
        }, 500)
      }
    })
  }, [])

  if (!isShown) return null

  return (
    <Portal container={document.body}>
      <div className="z-[999] fixed top-0 left-0 w-full h-full bg-white backdrop-blur-sm bg-opacity-10 flex items-center justify-center">
        <div
          className={classNames(
            'bg-white border-2 border-gray-200 rounded-2xl p-8 max-w-md w-full mx-auto shadow-lg duration-300',
            {
              'opacity-0 -translate-y-2': !appearTransition,
            }
          )}
        >
          {/* Aninix Icon */}
          <div className="flex justify-center mb-6">
            <AninixIcon />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
            Don't lose your projects
          </h2>

          {/* Description */}
          <p className="text-gray-500 text-sm text-center mb-8 leading-tight font-normal whitespace-break-spaces">
            Login to save your work and access it from anywhere. Your projects
            may be lost if you continue without an account.
          </p>

          {/* Login with Figma Button */}
          <button
            onClick={onLoginWithFigma}
            className={classNames(
              'w-full bg-black text-white text-sm py-2.5 px-4 rounded-lg mb-3',
              'flex items-center justify-center gap-3',
              'font-medium text-base',
              'hover:bg-gray-800 transition-colors duration-200'
            )}
          >
            <FigmaIcon />
            Login with Figma
          </button>

          {/* Continue Anyway Button */}
          <button
            onClick={onContinueAnyway}
            className={classNames(
              'w-full bg-gray-100 text-gray-500 text-sm py-2.5 px-4 rounded-lg border border-gray-200',
              'font-medium text-base',
              'hover:bg-red/10 hover:border-red/20 hover:text-red transition-colors duration-200'
            )}
          >
            I understand the risk, continue anyway
          </button>
        </div>
      </div>
    </Portal>
  )
}

const AninixIcon: React.FCC<{}> = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="48" height="48" rx="24" fill="#374FD5" />
    <path
      d="M31.9688 23.5781L20.8594 33.4688V15.1875L16.9219 17.2504V37.0781L19.4062 39.0938L37.6875 22.6875L19.125 7.96875L15.1875 10.0312L31.9688 23.5781Z"
      fill="white"
    />
  </svg>
)

const FigmaIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.66667 20C8.50762 20 10 18.5076 10 16.6667V13.3333H6.66667C4.82572 13.3333 3.33333 14.8257 3.33333 16.6667C3.33333 18.5076 4.82572 20 6.66667 20Z"
      fill="#0ACF83"
    />
    <path
      d="M3.33333 10C3.33333 8.15905 4.82572 6.66667 6.66667 6.66667H10V13.3333H6.66667C4.82572 13.3333 3.33333 11.8409 3.33333 10Z"
      fill="#A259FF"
    />
    <path
      d="M3.33333 3.33333C3.33333 1.49238 4.82572 0 6.66667 0H10V6.66667H6.66667C4.82572 6.66667 3.33333 5.17429 3.33333 3.33333Z"
      fill="#F24E1E"
    />
    <path
      d="M10 0H13.3333C15.1743 0 16.6667 1.49238 16.6667 3.33333C16.6667 5.17429 15.1743 6.66667 13.3333 6.66667H10V0Z"
      fill="#FF7262"
    />
    <path
      d="M16.6667 10C16.6667 11.8409 15.1743 13.3333 13.3333 13.3333C11.4924 13.3333 10 11.8409 10 10C10 8.15905 11.4924 6.66667 13.3333 6.66667C15.1743 6.66667 16.6667 8.15905 16.6667 10Z"
      fill="#1ABCFE"
    />
  </svg>
)
